import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Table, TableBody, TableCell, TableHead, TableRow, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Snackbar, Alert, CircularProgress, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VerifiedIcon from '@mui/icons-material/Verified';
import TranslateIcon from '@mui/icons-material/Translate';
import FolderIcon from '@mui/icons-material/Folder';
import SyncIcon from '@mui/icons-material/Sync';
import api from '../../api';

const MasterSettings = () => {
  const theme = useTheme();
  const { dashboard } = theme;
  
  const [activeTab, setActiveTab] = useState('departments');
  const [dataList, setDataList] = useState([]);
  
  const [name, setName] = useState('');
  const [hierarchyLevel, setHierarchyLevel] = useState(1);
  const [salaryMin, setSalaryMin] = useState(0);
  const [salaryMax, setSalaryMax] = useState(0);

  const todayStr = new Date().toISOString().split('T')[0];
  const [syncStartDate, setSyncStartDate] = useState(todayStr);
  const [syncEndDate, setSyncEndDate] = useState(todayStr);
  const [syncing, setSyncing] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });

  const endpoints = {
    departments: '/settings/departments',
    designations: '/settings/designations',
    education: '/settings/education',
    experience: '/settings/experience',
    skills: '/settings/skills',
    certifications: '/settings/certifications',
    languages: '/settings/languages',
    document_categories: '/settings/document_categories'
  };

  useEffect(() => {
    if (activeTab !== 'biometric_sync') fetchData();
    // eslint-disable-next-line
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const res = await api.get(endpoints[activeTab]);
      setDataList(res.data);
    } catch (err) { console.error(err); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!name) return showMsg("Name is required", "error");

    let payload = { name };
    if (activeTab === 'departments') payload.hierarchy_level = parseInt(hierarchyLevel);
    if (activeTab === 'designations') {
      payload.salary_range_min = parseFloat(salaryMin);
      payload.salary_range_max = parseFloat(salaryMax);
    }

    try {
      await api.post(endpoints[activeTab], payload);
      showMsg("Added Successfully!");
      setName(''); setHierarchyLevel(1); setSalaryMin(0); setSalaryMax(0);
      fetchData();
    } catch (err) { showMsg(err.response?.data?.detail || "Failed to add", "error"); }
  };

  const handleManualSync = async () => {
    if (new Date(syncStartDate) > new Date(syncEndDate)) return showMsg("Start Date cannot be after End Date.", "error");
    setSyncing(true);
    try {
      const response = await api.post(`/attendance/manual-sync?start_date=${syncStartDate}&end_date=${syncEndDate}`);
      showMsg(response.data.message, 'success');
    } catch (error) { showMsg(error.response?.data?.detail || error.message, 'error'); } 
    finally { setSyncing(false); }
  };

  // Icons mapping for tabs
  const getTabIcon = (id) => {
    const iconMap = {
      departments: <BusinessIcon fontSize="small" />,
      designations: <WorkIcon fontSize="small" />,
      education: <SchoolIcon fontSize="small" />,
      experience: <TrendingUpIcon fontSize="small" />,
      skills: <PsychologyIcon fontSize="small" />,
      certifications: <VerifiedIcon fontSize="small" />,
      languages: <TranslateIcon fontSize="small" />,
      document_categories: <FolderIcon fontSize="small" />,
      biometric_sync: <SyncIcon fontSize="small" />
    };
    return iconMap[id] || <SettingsIcon fontSize="small" />;
  };

  const tabs = [
    { id: 'departments', label: 'Departments' },
    { id: 'designations', label: 'Designations' },
    { id: 'education', label: 'Education Levels' },
    { id: 'experience', label: 'Experience Ranges' },
    { id: 'skills', label: 'Core Skills' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'languages', label: 'Languages' },
    { id: 'document_categories', label: 'Document Categories' },
    { id: 'biometric_sync', label: 'Tracker Sync' } 
  ];

  // NavGroupHeader - EXACT MATCH FROM DASHBOARD
  const NavGroupHeader = ({ title }) => (
    <Typography 
      variant="caption" 
      sx={{ 
        px: dashboard.sectionHeading.paddingX,
        py: dashboard.sectionHeading.paddingY,
        display: 'block', 
        color: dashboard.sectionHeading.color,
        fontWeight: dashboard.sectionHeading.fontWeight,
        mt: dashboard.sectionHeading.marginTop,
        fontSize: dashboard.sectionHeading.fontSize,
        letterSpacing: dashboard.sectionHeading.letterSpacing,
        textTransform: dashboard.sectionHeading.textTransform,
      }}
    >
      {title}
    </Typography>
  );

  // NavItem - EXACT MATCH FROM DASHBOARD
  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: dashboard.navItem.marginBottom }}>
      <ListItemButton 
        selected={activeTab === id} 
        onClick={() => setActiveTab(id)}
        sx={{
          borderRadius: dashboard.navItem.borderRadius,
          mx: dashboard.navItem.paddingX,
          py: dashboard.navItem.paddingY,
          '&.Mui-selected': { 
            bgcolor: 'primary.main', 
            color: 'white', 
            '&:hover': { bgcolor: 'primary.dark' } 
          },
          '&.Mui-selected .MuiListItemIcon-root': { color: 'white' },
        }}
      >
        <ListItemIcon sx={{ 
          minWidth: dashboard.navItem.iconMinWidth, 
          color: activeTab === id ? 'white' : 'inherit' 
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label}
          primaryTypographyProps={{ 
            fontWeight: activeTab === id ? 'bold' : 'medium', 
            fontSize: dashboard.navItem.primaryFontSize
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Paper elevation={2} sx={{ 
      display: 'flex', 
      height: dashboard.paper.minHeight,
      width: '100%', 
      borderRadius: dashboard.paper.borderRadius, 
      overflow: 'hidden',
      mt: dashboard.paper.marginTop
    }}>
      
      {/* LEFT SIDEBAR - EXACT MATCH FROM DASHBOARD */}
      <Box sx={{ 
        width: dashboard.sidebar.width,
        minWidth: dashboard.sidebar.width,
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: dashboard.sidebar.paddingTop,
        pb: dashboard.sidebar.paddingBottom,
        px: dashboard.sidebar.paddingX,
        boxShadow: '1px 0 3px rgba(0,0,0,0.04)',
        zIndex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        <List component="nav" dense sx={{ py: 0 }}>
          
          <NavGroupHeader title="MASTER CONFIGURATION" />
          {tabs.map(tab => (
            <NavItem 
              key={tab.id} 
              id={tab.id} 
              label={tab.label} 
              icon={getTabIcon(tab.id)}
            />
          ))}

        </List>
      </Box>

      {/* RIGHT CONTENT AREA - EXACT MATCH FROM DASHBOARD */}
      <Box sx={{ 
        flexGrow: 1, 
        p: dashboard.content.padding,
        width: '100%', 
        bgcolor: '#fbfbfb', 
        overflowY: 'auto',
        overflowX: 'hidden',
        height: '100%',
        '&::-webkit-scrollbar': {
          width: 6,
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: 3,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#a8a8a8',
        },
      }}>
        {activeTab === 'biometric_sync' ? (
          <Paper elevation={0} sx={{ 
            p: 3, 
            mb: 3, 
            bgcolor: 'transparent'
          }}>
            <Typography 
              variant="h6" 
              color="primary" 
              gutterBottom 
              sx={{ 
                textTransform: 'uppercase', 
                fontWeight: 'bold',
                fontSize: '1rem',
                letterSpacing: 0.5
              }}
            >
              Manual Biometric Synchronization
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 3, 
              alignItems: 'center', 
              mt: 2,
              flexWrap: 'wrap'
            }}>
              <TextField 
                size="small" 
                type="date" 
                label="Start Date" 
                InputLabelProps={{ shrink: true }} 
                value={syncStartDate} 
                onChange={e => setSyncStartDate(e.target.value)} 
                sx={{ minWidth: 200 }} 
              />
              <TextField 
                size="small" 
                type="date" 
                label="End Date" 
                InputLabelProps={{ shrink: true }} 
                value={syncEndDate} 
                onChange={e => setSyncEndDate(e.target.value)} 
                sx={{ minWidth: 200 }} 
              />
              <Button 
                variant="contained" 
                disableElevation 
                onClick={handleManualSync} 
                disabled={syncing} 
                startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <HistoryIcon />}
              >
                {syncing ? "Fetching Data..." : "Execute Sync"}
              </Button>
            </Box>
          </Paper>
        ) : (
          <>
            <Paper elevation={0} sx={{ 
              p: 0, 
              mb: 3, 
              bgcolor: 'transparent'
            }}>
              <Typography 
                variant="h6" 
                color="primary" 
                gutterBottom 
                sx={{ 
                  textTransform: 'uppercase', 
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  letterSpacing: 0.5
                }}
              >
                Add New {activeTab.replace('_', ' ')}
              </Typography>
              <Box 
                component="form" 
                onSubmit={handleAdd} 
                sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'center', 
                  flexWrap: 'wrap',
                  mt: 1
                }}
              >
                <TextField 
                  size="small" 
                  label="Name / Title" 
                  required 
                  value={name} 
                  onChange={e => setName(e.target.value)} 
                  sx={{ minWidth: 250 }} 
                />
                {activeTab === 'departments' && (
                  <TextField 
                    size="small" 
                    type="number" 
                    label="Hierarchy Level" 
                    value={hierarchyLevel} 
                    onChange={e => setHierarchyLevel(e.target.value)} 
                    sx={{ minWidth: 150 }}
                  />
                )}
                {activeTab === 'designations' && (
                  <>
                    <TextField 
                      size="small" 
                      type="number" 
                      label="Min Salary" 
                      value={salaryMin} 
                      onChange={e => setSalaryMin(e.target.value)} 
                      sx={{ minWidth: 150 }}
                    />
                    <TextField 
                      size="small" 
                      type="number" 
                      label="Max Salary" 
                      value={salaryMax} 
                      onChange={e => setSalaryMax(e.target.value)} 
                      sx={{ minWidth: 150 }}
                    />
                  </>
                )}
                <Button 
                  type="submit" 
                  variant="contained" 
                  disableElevation
                >
                  Add Record
                </Button>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ 
              p: 0, 
              bgcolor: 'transparent'
            }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Name</TableCell>
                    {activeTab === 'departments' && (
                      <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Level</TableCell>
                    )}
                    {activeTab === 'designations' && (
                      <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Salary Range</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataList.map(item => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.id}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      {activeTab === 'departments' && (
                        <TableCell>{item.hierarchy_level}</TableCell>
                      )}
                      {activeTab === 'designations' && (
                        <TableCell>{item.salary_range_min} - {item.salary_range_max}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={3000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.type} 
          variant="filled" 
          sx={{ width: '100%' }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default MasterSettings;